'use client';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { useRollbarPerson } from '@rollbar/react';
import {
  useActiveAccount,
  useActiveLab,
  useActivePractice,
  useActiveTrial,
  useCurrentUser,
  useDispatch,
  useIsAccountLocked,
} from '@witmetrics/data-store';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import { DIALOGS, OVERLAYS } from '@witmetrics/unison-core';
import type { Dictionary } from '@witmetrics/utils';
import { currentDate } from '@witmetrics/utils/time';
import { useAppState } from '@/providers/AppStateProvider';
import { showErrorToaster, showWarningToaster } from '@/utils/toasters';
import { getAuthUrl, getPortalUrl } from '@/api/urls';
import { API } from '@/api';
import { isInput } from '@/utils/dom';
import { isLockedOut } from '@/utils/trials';
import { SHORTCUT_KEYS } from '@/constants/shortcutKeys';
import {
  LAB_NAVIGATION_KEYS,
  PRACTICE_NAVIGATION_KEYS,
} from '@/constants/navigationKeys';
import { usePath } from '@/hooks/usePath';
import { useMountEffect } from '@/hooks/useMountEffect';
import type {
  LabNavigationKey,
  NavigationKey,
  PracticeNavigationKey,
  ShortcutKey,
} from '@/types';
import AppHeader from '@/components/AppHeader';
import DialogsManager from '@/components/DialogsManager';
import OverlaysManager from '@/components/OverlaysManager';
import CheckingAuth from '@/components/CheckingAuth';
import AccountLockout from '@/components/AccountLockout';
import CaseOverlay from '@/overlays/CaseOverlay';
import PracticeOrderOverlay from '@/overlays/PracticeOrderOverlay';
import LabOrderOverlay from '@/overlays/LabOrderOverlay';
import PatientOverlay from '@/overlays/PatientOverlay';
import ScanServiceOverlay from '@/overlays/ScanServiceOverlay';
import AppSidebar from '@/components/AppSidebar';
import {
  buildRollbarPerson,
  classes,
  fetchLabData,
  fetchPracticeData,
  getNavigation,
  getShortcut,
  isIgnoredKey,
} from './utils';

export default function Layout({ children }: PropsWithChildren) {
  const shiftCount = useRef(0);
  const dispatch = useDispatch();
  const {
    onApiError,
    activeProjectId,
    activeOrderId,
    activeServiceId,
    activeLeadId,
    activeDialogs,
    setActiveDialogs,
    onSelectProject,
    onSelectLead,
    onSelectOrder,
    onOpenAgenda,
  } = useAppState();
  const { searchParams, navigate } = usePath();
  const activeAccount = useActiveAccount();
  const activePractice = useActivePractice();
  const activeLab = useActiveLab();
  const activeTrial = useActiveTrial();
  const isAccountLocked = useIsAccountLocked();
  const currentUser = useCurrentUser();

  try {
    // Update Rollbar with the current user
    useRollbarPerson(buildRollbarPerson(currentUser));
  } catch (err) {
    // Just means RollbarProvider isn't mounted
  }

  const isLocked = isLockedOut(isAccountLocked, activeTrial);

  useMountEffect(() => {
    handleFetchCurrentUser();
    checkActiveParams();
  });

  useEffect(() => {
    const checkKeyPress = (e: KeyboardEvent) => {
      return handleKeyPress(e, activeDialogs);
    };
    window.addEventListener('keyup', checkKeyPress);
    return () => {
      window.removeEventListener('keyup', checkKeyPress);
    };
  }, [activeDialogs]);

  useEffect(() => {
    if (activeAccount?.portalOnly) navigate(getPortalUrl());
  }, [activeAccount]);

  useEffect(() => {
    if (activePractice && !isLocked) handleFetchPracticeData();
  }, [activePractice, isLocked]);

  useEffect(() => {
    if (activeLab && !isLocked) handleFetchLabData();
  }, [activeLab, isLocked]);

  const checkActiveParams = () => {
    const projectId = searchParams.get('activeProjectId');
    const leadId = searchParams.get('activeLeadId');
    const orderId = searchParams.get('activeOrderId');
    if (projectId && parseInt(projectId) !== activeProjectId) {
      onSelectProject(parseInt(projectId));
    } else if (orderId && parseInt(orderId) !== activeOrderId) {
      onSelectOrder(parseInt(orderId));
    } else if (leadId && parseInt(leadId) !== activeLeadId) {
      onSelectLead(parseInt(leadId));
    }
  };

  const handleFetchCurrentUser = async () => {
    try {
      dispatch.addCurrentUser(await API.Users.fetchCurrentUser());
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        navigate(getAuthUrl(), { redirect: window.location.href });
      } else {
        showErrorToaster('Error fetching current user');
        throw err;
      }
    }
  };

  const handleFetchPracticeData = async () => {
    try {
      const data = await fetchPracticeData(activePractice!.id);
      dispatch.addPracticePreferences(data.practicePreferences);
      dispatch.addPracticeUsers(data.practiceUsers);
      dispatch.addLabPractices(data.practiceLabs);
    } catch (err) {
      onApiError(err, 'Error fetching practice settings', () =>
        handleFetchPracticeData()
      );
    }
  };

  const handleFetchLabData = async () => {
    try {
      const data = await fetchLabData(activeLab!.id);
      dispatch.addLabPreferences(data.labPreferences);
      dispatch.addLabUsers(data.labUsers);
      dispatch.addLabPractices(data.labPractices);
    } catch (err) {
      onApiError(err, 'Error fetching lab settings', () =>
        handleFetchLabData()
      );
    }
  };

  const handleSelectShortcut = (shortcut: ShortcutKey) => {
    let updates: { [d: string]: boolean } = { [DIALOGS.SHORTCUTS]: false };
    const key = SHORTCUT_KEYS[shortcut];
    if (key === OVERLAYS.AGENDA) onOpenAgenda(currentDate());
    else updates[key] = true;
    setActiveDialogs((dialogs) => ({ ...dialogs, ...updates }));
  };

  const handleSelectNavigation = (shortcut: NavigationKey) => {
    setActiveDialogs((d) => ({
      ...d,
      [DIALOGS.NAVIGATION]: false,
    }));
    if (activePractice) {
      navigate(PRACTICE_NAVIGATION_KEYS[shortcut as PracticeNavigationKey]);
    } else {
      navigate(LAB_NAVIGATION_KEYS[shortcut as LabNavigationKey]);
    }
  };

  const handleKeyPress = (e: KeyboardEvent, dialogs: Dictionary<boolean>) => {
    if (isInput(e)) return null;
    const isSearchDialogShown = dialogs[DIALOGS.SEARCH];
    const isShortcutsDialogShown = dialogs[DIALOGS.SHORTCUTS];
    const isNavigationDialogShown = dialogs[DIALOGS.NAVIGATION];
    if (e.key === 'Escape' && isSearchDialogShown) {
      shiftCount.current = 0;
      return setActiveDialogs((d) => ({
        ...d,
        [DIALOGS.SEARCH]: false,
        [DIALOGS.SHORTCUTS]: false,
        [DIALOGS.NAVIGATION]: false,
      }));
    }
    if (['\\', ':'].includes(e.key) && !isNavigationDialogShown) {
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.SHORTCUTS]: true }));
    }
    if (e.key === '/' && !isShortcutsDialogShown) {
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.NAVIGATION]: true }));
    }
    if (isShortcutsDialogShown) {
      const shortcut = getShortcut(e);
      if (shortcut) return handleSelectShortcut(shortcut);
    } else if (isNavigationDialogShown) {
      const shortcut = getNavigation(e);
      if (shortcut) return handleSelectNavigation(shortcut);
    }
    if (isShortcutsDialogShown || isNavigationDialogShown) {
      if (isIgnoredKey(e)) return; // Ignore shift key since it's probably just being used as a modifier key
      return showWarningToaster(`Unrecognized shortcut: ${e.key}`);
    }
    if (e.key !== 'Shift' || isSearchDialogShown) return null;
    if (shiftCount.current === 1) {
      shiftCount.current = 0;
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.SEARCH]: true }));
    } else if (shiftCount.current === 0) {
      shiftCount.current = 1;
      // Stop listening if there's more than half a second between key presses
      setTimeout(() => (shiftCount.current = 0), 500);
    } else {
      shiftCount.current = 0;
    }
  };

  if (!currentUser) return <CheckingAuth />;

  if (isLocked) return <AccountLockout />;

  return (
    <div className={classes.wrapper}>
      {activeLeadId && <PatientOverlay />}
      {activeProjectId && <CaseOverlay />}
      {activeOrderId && activePractice && <PracticeOrderOverlay />}
      {activeOrderId && activeLab && <LabOrderOverlay />}
      {activeServiceId && <ScanServiceOverlay />}
      <DialogsManager
        onSelectShortcut={handleSelectShortcut}
        onSelectNavigation={handleSelectNavigation}
      />
      <OverlaysManager />
      <div className={classes.screen}>
        <AppHeader />
        <div className={classes.body}>
          <AppSidebar />
          <div className={classes.content}>{children}</div>
        </div>
      </div>
    </div>
  );
}
